import { h as __h } from "preact";
import { signal } from "@preact/signals";
import { Component } from "preact";
/** @import { ComponentChildren } from "preact"; */

import { hostResponse } from "~/backend/validate.js";
import { query, paginatedResponse } from "~/lib/api.js";
import { CDN } from "~/lib/consts.js";
import { identity } from "~/lib/identity.js";
import { machineActions } from "~/lib/machineAccess.js";
import * as msg from "~/lib/ws/msg.js";
/** @import WS from "~/lib/ws/ws.js"; */

import html from "~/util/html.js";
import Query from "~/util/query.js";
import Avatar from "~/component/Avatar.js";
import Button from "~/component/Button.js";
import ComputerCard, { ComputerList } from "~/component/ComputerCard.js";
import Data from "~/component/Data.js";
import Icon from "~/component/Icon.js";
import ReceivedInvites from "~/component/ReceivedInvites.js";
import Section from "~/component/Section.js";
import ShareModal from "~/component/ShareModal.js";
import WSListener from "~/component/WSListener.js";
import adminStyle from "~/admin/lib/style.js";
import { connect } from "~/admin/res/computer/actions/connect.js";
import button from "~/style/button.js";
import util from "~/style/util.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./Computers.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./Computers.module.css", import.meta.url);
const css = adopt(sheet);
const INSTALLER_URL = `${CDN}/builds/stable/channels/release/phaze-installer.exe`;

/**
 * @extends {Component<Props>}
 *
 * @typedef {object} Props
 * @property {WS} ws
 */
export default class Computers extends Component {
  hosts = query(() => ({
    key: ["/v2/hosts", {
      show_offline: true,
      limit: 100
    }]
  }), {
    schema: paginatedResponse(hostResponse)
  });
  #canRefresh = signal(true);

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #refreshTimer;
  #sharing = signal(/** @type {{ host: hostResponse; open: boolean } | null} */null);
  #renderShareModal() {
    const sharing = this.#sharing.value;
    if (!sharing) return;
    const {
      host
    } = sharing;
    // keep the host so the modal's title stays put through the close transition
    const onClose = () => {
      this.#sharing.value = {
        host,
        open: false
      };
    };
    return __h(ShareModal, {
      subscribe: (/** @type {() => void} */onRefresh, /** @type {AbortSignal} */signal) => {
        this.props.ws.on("message", ({
          detail
        }) => {
          const {
            action
          } = detail;
          if (action === msg.HOST_REFRESH || action === msg.MACHINE_ACCESS_INVITE_REFRESH) onRefresh();
        }, {
          signal
        });
      },
      id: host.id,
      name: host.name,
      open: sharing.open,
      onClose: onClose
    });
  }

  /** @override */
  componentWillUnmount() {
    clearTimeout(this.#refreshTimer);
  }

  // refreshing is throttled so it can't be used to hammer the API
  #renderRefresh() {
    return __h(Button, {
      type: "button",
      size: "small",
      disabled: !this.#canRefresh.value,
      loading: this.hosts.loading,
      onClick: () => {
        this.hosts.fetch();
        Query.invalidate(["/v2/access-invites"]);
        this.#canRefresh.value = false;
        this.#refreshTimer = setTimeout(() => {
          this.#canRefresh.value = true;
        }, 10_000);
      }
    }, __h(Icon, {
      name: "refresh",
      size: 12
    }), __h("span", {}, "Refresh"));
  }

  /**
   * @param {hostResponse[]} hosts
   * @param {(host: hostResponse) => ComponentChildren} [subtitleFor]
   */
  #list(hosts, subtitleFor) {
    return __h(ComputerList, {
      hosts: hosts,
      card: (/** @type {hostResponse} */host) => __h(ComputerCard, {
        host: host,
        connect: host.is_online && __h(Button, {
          level: "primary",
          onClick: () => connect(host.id)
        }, "Connect"),
        actions: machineActions(host, () => {
          this.#sharing.value = {
            host,
            open: true
          };
        }),
        subtitle: subtitleFor?.(host)
      })
    });
  }
  render() {
    const userId = identity.me?.id;
    const hosts = this.hosts.data?.data ?? [];
    // machines without an owner are managed machines an organization has assigned to this user
    const owned = hosts.filter(host => !host.owner_id || host.owner_id === userId);
    const shared = hosts.filter(host => host.owner_id && host.owner_id !== userId);
    return [__h("div", {
      "class": css.column
    }, this.hosts.data && owned.length === 0 && __h(DownloadBanner, {}), __h(Data, {
      loading: !this.hosts.data,
      error: this.hosts.error
    }, __h(Section, {
      title: "Your computers",
      icon: "machine",
      bare: true,
      actions: __h("div", {
        "class": adminStyle.actions
      },
      // enterprise members can't receive invites, since their organization assigns their computers
      !identity.enterprise && __h(ReceivedInvites, {}), this.#renderRefresh())
    }, owned.length > 0 ? this.#list(owned) : __h("div", {
      "class": util.card + " " + css.empty
    }, identity.enterprise ? "Your organization hasn't assigned you any computers yet." : "You haven't added any computers yet.")), shared.length > 0 && __h(Section, {
      title: "Shared with you",
      icon: "users",
      bare: true
    }, this.#list(shared, host => host.owner && __h("span", {
      "class": css.person,
      title: host.owner.email
    }, __h(Avatar, {
      name: host.owner.name,
      size: 20
    }), __h("span", {}, host.owner.name)))))), this.#renderShareModal(), __h(WSListener, {
      ws: this.props.ws,
      "for": [msg.HOST_REFRESH],
      on: () => Query.invalidate(["/v2/hosts"])
    }), __h(WSListener, {
      ws: this.props.ws,
      "for": [msg.MACHINE_ACCESS_INVITE_REFRESH],
      on: () => Query.invalidate(["/v2/access-invites"])
    })];
  }
}
function DownloadBanner() {
  return __h("div", {
    "class": css.download
  }, __h("div", {
    "class": css.downloadCopy
  }, __h("h2", {
    "class": css.downloadTitle
  }, "Download Phaze"), __h("p", {
    "class": css.downloadBlurb
  }, "The Phaze desktop app lets you connect to your computers from anywhere.")), __h("a", {
    "class": button.button + " " + css.downloadButton,
    href: INSTALLER_URL,
    download: true
  }, __h("span", {
    "class": css.downloadLabel
  }, __h(Icon, {
    name: "download-binary",
    size: 16
  }), "Download for Windows")));
}